import React, { useState, useCallback } from 'react';
import { createStore } from 'redux';
import { Provider } from 'react-redux';
import { applyMiddleware } from 'helpers/redux_dev_tools';
import thunk from 'redux-thunk';
import PropTypes from 'prop-types';
import { Button, Modal } from 'react-bootstrap-5';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

import NewPropertyFromExisting from './new_listing_modal/new_property_from_existing';
import NewPropertyFromScratch from './new_listing_modal/new_property_from_scratch';
import IntroStep from './new_listing_modal/intro_step';
import reducers from './new_listing_modal/reducers';
import { normalizeBrokerOptions } from './new_listing_modal/helpers/normalizers';
import { countryListPropType, brokerTuple } from './new_listing_modal/prop_types';
import { AB_SEGMENTS } from './new_listing_modal/create_property_form';

import { post } from 'helpers/api';
import {
  maxHeightModal,
  confirmationModalBackdrop,
  confirmationMaxWidthModal
} from './new_listing_modal/styles.module.scss';
export default function NewListingModalReduxWrapper({
  allowUploadDoc,
  buttonAbSegment,
  countryList,
  crmAccess,
  defaultBrokerId,
  defaultCountryId,
  edgeCrmAccess,
  showCreateDocButton,
  hasInsights,
  maxBrokersLimit,
  propertyTypes,
  brokerOptionTuples
}) {
  const initialStoreData = {
    brokerSelectOptions: normalizeBrokerOptions(brokerOptionTuples), // readonly
    propertyTypes: propertyTypes, // readonly
    newPropertyFromScratchLocation: {
      country: countryList.find(country => country.id === defaultCountryId) ||
        countryList.find(country => country.name === 'United States') ||
        countryList[0]
    }
  };

  const store = createStore(reducers, initialStoreData, applyMiddleware(thunk));
  if (module.hot) {
    module.hot.accept('./new_listing_modal/reducers/index', () => {
      store.replaceReducer(require('./new_listing_modal/reducers/index'));
    });
  }

  return (
    <Provider store={store}>
      <NewListingModal
        allowUploadDoc={allowUploadDoc}
        buttonAbSegment={buttonAbSegment}
        countryList={countryList}
        crmAccess={crmAccess}
        defaultBrokerId={defaultBrokerId}
        defaultCountryId={defaultCountryId}
        edgeCrmAccess={edgeCrmAccess}
        hasInsights={hasInsights}
        maxBrokersLimit={maxBrokersLimit}
        showCreateDocButton={showCreateDocButton}
      />
    </Provider>
  );
}

export function NewListingModal({
  allowUploadDoc,
  buttonAbSegment,
  countryList,
  crmAccess,
  defaultBrokerId,
  edgeCrmAccess,
  showCreateDocButton,
  hasInsights,
  maxBrokersLimit,
}) {
  const MODAL_STEPS = {
    INTRO: 'intro',
    FROM_SCRATCH: 'from_scratch',
    FROM_PROPERTY: 'from_property'
  };

  const hasAnyCrmAccess = crmAccess || edgeCrmAccess;

  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [errorMessages, setErrorMessages] = useState({});
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [isAiUploading, setIsAiUploading] = useState(false);
  const onFromExistingSelected = hasAnyCrmAccess ? () => setStep(MODAL_STEPS.FROM_PROPERTY) : null;
  const onFromScratchSelected = () => setStep(MODAL_STEPS.FROM_SCRATCH);

  // show intro if there's more than 1 choice
  const initialStep = onFromExistingSelected ? MODAL_STEPS.INTRO : MODAL_STEPS.FROM_SCRATCH;
  const [step, setStep] = useState(initialStep);

  const requestClose = () => {
    if (isAiUploading && !saving) {
      setShowExitConfirmation(true);
    } else {
      performClose();
    }
  };

  const performClose = () => {
    setStep(initialStep);
    setShow(false);
    setHasErrors(false);
    setShowExitConfirmation(false);
    //below safe because AiDocumentImport unmounts when the modal closes, closing the websocket
    setIsAiUploading(false);
  };

  const brokersAttributes = (brokerIds) => {
    if (defaultBrokerId) {
      return { '0': { primary: true, user_id: defaultBrokerId } };
    }

    return brokerIds.map((brokerId, index) => {
      return ({
        primary: index === 0,
        sortOrder: index,
        userId: brokerId
      });
    });
  };

  const save = ({
    edgeResearchPropertyId,
    researchPropertyId,
    brokerIds = [{}],
    propertyParams = {},
    researchPropertyParams = {},
    aiFeedback,
    aiIngestData,
    insightsId,
    createDocument
  }) => {
    setSaving(true);
    setErrorMessages({});
    setHasErrors(false);
    const params = {
      edgeResearchPropertyId: edgeResearchPropertyId,
      researchPropertyId: researchPropertyId,
      aiFeedback: aiFeedback,
      aiIngestData: aiIngestData,
      createDocument: createDocument,
      property: {
        // Important: create non-draft when creating doc, but proposal must stay true
        draft: createDocument ? false : true,
        saleAgreementAttributes: { proposal: true },
        leaseAgreementAttributes: { proposal: true },
        projectsUsersAttributes: brokersAttributes(brokerIds),
        ...propertyParams,
        research_property_attributes: researchPropertyParams
      },
    };

    post('/properties', params)
      .then(({ path }) => {
        const url = new URL(path, window.location.origin);
        if (insightsId) {
          url.searchParams.set('address_correction', insightsId);
        }
        window.location = url.toString();
      })
      .catch((error) => {
        setSaving(false);
        setHasErrors(true);
        setErrorMessages(error);
      });
  };

  const handleAiUploadStatusChange = useCallback((isUploading) => {
    setIsAiUploading(isUploading);
  }, []);

  const newPropertyScreen = (NewPropertyScreen, extraProps = {}) => {
    // leave unset to omit back button
    const gotoInitialStep = initialStep == MODAL_STEPS.INTRO ? (() => {
      if (isAiUploading && !saving) {
        setShowExitConfirmation(true);
      } else {
        setStep(MODAL_STEPS.INTRO);
        setHasErrors(false);
      }
    }) : null;

    const newListingProps = {
      gotoPrevStep: gotoInitialStep,
      saving,
      showBrokerSelect: !defaultBrokerId,
      onSave: save,
      hasErrors,
      errorMessages,
      maxBrokersLimit,
      onAiUploadStatusChange: handleAiUploadStatusChange,
      ...extraProps
    };

    return <NewPropertyScreen {...newListingProps} />;
  };

  const modalContent = () => {
    if (step === MODAL_STEPS.INTRO) {
      return (
        <IntroStep
          onFromExistingSelected={onFromExistingSelected}
          onFromScratchSelected={onFromScratchSelected}
        />
      );
    } else if (step === MODAL_STEPS.FROM_PROPERTY) {
      return newPropertyScreen(NewPropertyFromExisting, { edgeCrmAccess });
    } else if (step === MODAL_STEPS.FROM_SCRATCH) {
      return newPropertyScreen(NewPropertyFromScratch, {
        allowUploadDoc,
        buttonAbSegment,
        countryList,
        showCreateDocButton,
        hasInsights
      });
    }
  };

  return (
    <>
      <button className="w-100 btn btn-primary" name="new-listing-button" type="button" onClick={() => setShow(true)}>
        New Listing
      </button>
      <Modal
        dialogClassName={maxHeightModal}
        show={show}
        style={{ '--animate-duration': '0.3s' }}
        onHide={requestClose}
      >
        <SwitchTransition mode="out-in">
          <CSSTransition
            classNames={{
              enter: 'animate__animated',
              enterActive: 'animate__fadeIn',
              exit: 'animate__animated',
              exitActive: 'animate__fadeOut'
            }}
            key={step}
            timeout={500}
          >
            {modalContent()}
          </CSSTransition>
        </SwitchTransition>
      </Modal>
      <Modal
        backdropClassName={confirmationModalBackdrop}
        dialogClassName={confirmationMaxWidthModal}
        show={showExitConfirmation}
        style={{ zIndex: 1070 }} // above main modal (1050)
        onHide={() => setShowExitConfirmation(false)}
      >
        <Modal.Header>
          <Modal.Title>Stop Processing?</Modal.Title>
        </Modal.Header>
        <Modal.Body>Your documents are still processing. Are you sure you want to exit?</Modal.Body>
        <Modal.Footer>
          <Button
            data-pendo-tag="pef_ai_upload_exit_processing_button"
            variant="outline"
            onClick={performClose}
          >
            Exit
          </Button>
          <Button
            data-pendo-tag="pef_ai_upload_keep_processing_button"
            variant="secondary"
            onClick={() => setShowExitConfirmation(false)}
          >
            Keep Processing
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

const sharedProps = {
  allowUploadDoc: PropTypes.bool,
  buttonAbSegment: PropTypes.oneOf(Object.values(AB_SEGMENTS)),
  countryList: countryListPropType.isRequired,
  crmAccess: PropTypes.bool.isRequired,
  defaultBrokerId: PropTypes.number,
  edgeCrmAccess: PropTypes.bool,
  hasInsights: PropTypes.bool.isRequired,
  maxBrokersLimit: PropTypes.number.isRequired,
  showCreateDocButton: PropTypes.bool,
};

NewListingModalReduxWrapper.propTypes = {
  ...sharedProps,
  brokerOptionTuples: PropTypes.arrayOf(brokerTuple).isRequired,
  defaultCountryId: PropTypes.number,
  propertyTypes: PropTypes.objectOf(PropTypes.string)
};

NewListingModal.propTypes = sharedProps;
